/**
 * Pure rules over the category catalog: lookup, search, and the special
 * account resolution for representation, the asset threshold and foreign
 * SaaS. No database access.
 */
import { ENKLARE_FORTARING_MAX_PER_PERSON_ORE, halfPrisbasbeloppFor } from '../bas/constants';
import { isReverseCharge } from '../bas/momskoder';
import type { Ore } from '../money';
import type { ProposalQuestion, VatChoice } from '../types';
import type { AssetChoice, Category } from './types';

export const ASSET_ACCOUNT = 1220;
export const REPRESENTATION_DEDUCTIBLE_ACCOUNT = 6071;
export const REPRESENTATION_NON_DEDUCTIBLE_ACCOUNT = 6072;
export const FOREIGN_SAAS_ACCOUNT = 6540;

export type MealType = 'meal' | 'light';

export function findCategory(catalog: readonly Category[], id: string): Category | undefined {
  return catalog.find((c) => c.id === id);
}

/** Enklare förtäring: at most 60 kr per person excluding VAT. */
export function isLightRefreshment(netOre: Ore, persons: number): boolean {
  const p = Math.max(1, Math.floor(persons));
  return netOre <= p * ENKLARE_FORTARING_MAX_PER_PERSON_ORE;
}

/**
 * Which representation account applies. The user's answer "enklare förtäring"
 * only holds when the amount really is at most 60 kr per person; above that
 * the law treats it as a meal (6072).
 */
export function representationAccountFor(mealType: MealType, netOre: Ore, persons: number): number {
  if (mealType === 'light' && isLightRefreshment(netOre, persons)) return REPRESENTATION_DEDUCTIBLE_ACCOUNT;
  return REPRESENTATION_NON_DEDUCTIBLE_ACCOUNT;
}

/** Inventarier: net price at or above half a prisbasbelopp is an asset to depreciate. */
export function isAssetAboveThreshold(netOre: Ore, rulesYear: number): boolean {
  return netOre >= halfPrisbasbeloppFor(rulesYear);
}

export type AssetReason = 'above_threshold' | 'below_threshold';

/**
 * What the half-prisbasbelopp rule decided for a purchase, and why, so the
 * card can say so in one line and offer the other answer.
 */
export interface AssetDecision {
  /** What the draft books. */
  books: AssetChoice;
  /** What the amount alone says. */
  suggested: AssetChoice;
  reason: AssetReason;
  /** Half a prisbasbelopp for the rules year, for the sentence. */
  thresholdOre: Ore;
  /** The user answered against the amount. */
  overridden: boolean;
}

/**
 * The amount is the only trigger: at or above half a prisbasbelopp the
 * purchase is an inventarie, below it a cost. The user may overrule it either
 * way — an expensive thing worn out within the year is a cost, and a cheap
 * one that lasts may be capitalised (K2 lets it, it does not demand it) — and
 * an answer that agrees with the amount is not an override, so choosing back
 * clears the marker by itself.
 */
export function assetDecision(netOre: Ore, rulesYear: number, override?: AssetChoice | null): AssetDecision {
  const thresholdOre = halfPrisbasbeloppFor(rulesYear);
  const above = netOre >= thresholdOre;
  const suggested: AssetChoice = above ? 'asset' : 'expense';
  const books = override ?? suggested;
  return { books, suggested, reason: above ? 'above_threshold' : 'below_threshold', thresholdOre, overridden: books !== suggested };
}

export interface ResolveAccountInput {
  vat: Pick<VatChoice, 'mode' | 'rate'>;
  /** Net amount (excl. deductible VAT) the account will carry. */
  netOre: Ore;
  rulesYear: number;
  persons?: number | undefined;
  mealType?: MealType | undefined;
  /** The user overruled the amount in either direction; see TransactionOptions. */
  assetChoice?: AssetChoice | undefined;
}

export interface ResolvedAccount {
  accountNumber: number;
  /** Which rule fired, if any. */
  rule?: 'representation_meal' | 'representation_light' | 'asset' | 'foreign_saas';
}

/** The account a category books on after its special rule is applied. */
export function resolveCategoryAccount(category: Category, input: ResolveAccountInput): ResolvedAccount {
  switch (category.rule) {
    case undefined:
      return { accountNumber: category.accountNumber };
    case 'representation': {
      const persons = input.persons ?? 1;
      const account = representationAccountFor(input.mealType ?? 'meal', input.netOre, persons);
      return {
        accountNumber: account,
        rule: account === REPRESENTATION_DEDUCTIBLE_ACCOUNT ? 'representation_light' : 'representation_meal',
      };
    }
    case 'asset_threshold':
      return assetDecision(input.netOre, input.rulesYear, input.assetChoice).books === 'asset'
        ? { accountNumber: ASSET_ACCOUNT, rule: 'asset' }
        : { accountNumber: category.accountNumber };
    case 'foreign_saas':
      return isReverseCharge(input.vat.mode)
        ? { accountNumber: FOREIGN_SAAS_ACCOUNT, rule: 'foreign_saas' }
        : { accountNumber: category.accountNumber };
    default: {
      const exhaustive: never = category.rule;
      throw new Error(`Unknown category rule ${String(exhaustive)}`);
    }
  }
}

/** Every account a category may end up on (for activation previews and tests). */
export function possibleAccountsFor(category: Category): number[] {
  switch (category.rule) {
    case undefined:
      return [category.accountNumber];
    case 'representation':
      return [REPRESENTATION_DEDUCTIBLE_ACCOUNT, REPRESENTATION_NON_DEDUCTIBLE_ACCOUNT];
    case 'asset_threshold':
      return [category.accountNumber, ASSET_ACCOUNT];
    case 'foreign_saas':
      return [category.accountNumber, FOREIGN_SAAS_ACCOUNT];
    default: {
      const exhaustive: never = category.rule;
      throw new Error(`Unknown category rule ${String(exhaustive)}`);
    }
  }
}

/** `ProposalQuestion.field` values a rule question may use, plus the editor-only meal type. */
export type RuleQuestionField = ProposalQuestion['field'] | 'mealType';

export interface RuleQuestion {
  /**
   * Which `TransactionInput` field the answer fills: `persons` and `vat` are
   * also `ProposalQuestion.field` members and can ride on `Proposal.question`;
   * `mealType` (`options.mealType`) is asked in the editor only.
   */
  field: RuleQuestionField;
  /** Spec wording, shown as the question label. */
  text: string;
  /** Choice questions list their options; free-number questions leave this empty. */
  options: { label: string; value: string }[];
  /** How the answer is entered: a choice chip row or a small integer field. */
  inputType: 'choice' | 'integer';
}

const PROPOSAL_QUESTION_FIELDS: readonly ProposalQuestion['field'][] = ['category', 'vat', 'payment', 'kind', 'persons'];

/** Whether a rule question can be carried on `Proposal.question` as the contract stands. */
export function isProposalQuestionField(field: RuleQuestionField): field is ProposalQuestion['field'] {
  return (PROPOSAL_QUESTION_FIELDS as readonly string[]).includes(field);
}

/**
 * The `Proposal.question` shape of a rule question, or undefined when the
 * contract cannot carry it (the meal-type question). Callers show those in
 * the editor instead.
 */
export function proposalQuestionFrom(question: RuleQuestion): ProposalQuestion | undefined {
  if (!isProposalQuestionField(question.field)) return undefined;
  return { field: question.field, text: question.text, options: question.options };
}

/**
 * The questions a category's rule needs answered, in the spec's wording
 * (section 10): representation asks "Antal personer" and "Måltid eller
 * enklare förtäring?"; foreign SaaS asks "Är säljaren utländsk?".
 *
 * Order: the persons question comes first because it can be carried on
 * `Proposal.question` and drives the 300 kr/person VAT cap; the meal type
 * defaults to 'meal' (the conservative, non-deductible reading) when unanswered.
 */
export function ruleQuestionsFor(category: Category): RuleQuestion[] {
  switch (category.rule) {
    case undefined:
    case 'asset_threshold':
      return [];
    case 'representation':
      return [
        { field: 'persons', text: 'Antal personer', inputType: 'integer', options: [] },
        {
          field: 'mealType',
          text: 'Måltid eller enklare förtäring?',
          inputType: 'choice',
          options: [
            { label: 'Måltid', value: 'meal' },
            { label: 'Enklare förtäring', value: 'light' },
          ],
        },
      ];
    case 'foreign_saas':
      return [
        {
          field: 'vat',
          text: 'Är säljaren utländsk?',
          inputType: 'choice',
          options: [
            { label: 'Ja, inom EU', value: 'rc_services_eu' },
            { label: 'Ja, utanför EU', value: 'rc_services_non_eu' },
            { label: 'Nej, svensk', value: 'domestic' },
          ],
        },
      ];
    default: {
      const exhaustive: never = category.rule;
      throw new Error(`Unknown category rule ${String(exhaustive)}`);
    }
  }
}

/**
 * First question of `ruleQuestionsFor` that fits `Proposal.question`, for
 * callers that show one at a time (the proposal card). Editor-only questions
 * (`mealType`) are skipped; use `ruleQuestionsFor` to render them all.
 */
export function ruleQuestionFor(category: Category): RuleQuestion | undefined {
  return ruleQuestionsFor(category).find((q) => isProposalQuestionField(q.field));
}

/**
 * Picker search: label, formal name, examples and keywords, case-insensitive.
 * Label prefix matches rank first, then keyword matches, then the rest.
 */
export function searchCatalog(catalog: readonly Category[], query: string, limit = 20): Category[] {
  const q = query.trim().toLowerCase();
  if (!q) return catalog.slice(0, limit);
  const scored: { c: Category; score: number }[] = [];
  for (const c of catalog) {
    const label = c.label.toLowerCase();
    let score = 0;
    if (label.startsWith(q)) score = 3;
    else if (label.includes(q)) score = 2;
    else if (c.keywords.some((k) => k.includes(q)) || c.examples.toLowerCase().includes(q)) score = 1;
    else if (c.formalName.toLowerCase().includes(q) || String(c.accountNumber).startsWith(q)) score = 1;
    if (score > 0) scored.push({ c, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.c);
}

/** Group → categories, in catalog order (for the picker's "Alla" section). */
export function groupCatalog(catalog: readonly Category[]): { group: string; categories: Category[] }[] {
  const out: { group: string; categories: Category[] }[] = [];
  for (const c of catalog) {
    let g = out.find((x) => x.group === c.group);
    if (!g) {
      g = { group: c.group, categories: [] };
      out.push(g);
    }
    g.categories.push(c);
  }
  return out;
}
